import { NOTIFICATIONS_QUERY_KEYS } from '@/features/notifications/hooks/use-notifications';
import { unreadCountQueryOptions } from '@/api/queries/notifications';
import { activityFeedQueryOptions } from '@/api/queries/activity';
import { currentUserQueryOptions } from '@/api/queries/users';
import { QueryClient, useQueryClient } from '@tanstack/react-query';

/**
 * Prefetch essential data for app initialization
 */
export class AppDataPrefetcher {
  private queryClient: QueryClient;

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient;
  }

  /**
   * Prefetch current user profile data
   * This is called during app initialization for better UX
   * Uses domain-oriented query options
   * @param userId - The current user's ID (required for API endpoint)
   */
  async prefetchCurrentUserProfile(userId: string): Promise<void> {
    try {
      if (!userId) {
        console.warn('Cannot prefetch profile: no user ID available');
        return;
      }

      await this.queryClient.prefetchQuery(currentUserQueryOptions(userId));

    } catch (error) {
      console.warn('Failed to prefetch current user profile:', error);
      // Don't throw - prefetching failures should not break app initialization
    }
  }

  /**
   * Prefetch the first page of the activity feed (default landing screen)
   */
  async prefetchActivityFeed(): Promise<void> {
    try {
      await this.queryClient.prefetchInfiniteQuery(activityFeedQueryOptions());
    } catch (error) {
      console.warn('Failed to prefetch activity feed:', error);
    }
  }

  /**
   * Prefetch unread notifications count
   * This is called during app initialization to show badge count immediately
   */
  async prefetchUnreadCount(): Promise<void> {
    try {
      await this.queryClient.prefetchQuery(unreadCountQueryOptions());
    } catch (error) {
      console.warn('Failed to prefetch unread count:', error);
      // Don't throw - prefetching failures should not break app initialization
    }
  }

  /**
   * Prefetch multiple essential data points for the app
   * @param userId - The current user's ID (required for profile prefetch)
   */
  async prefetchEssentialData(userId?: string): Promise<void> {

    const prefetchPromises: Promise<void>[] = [];

    if (userId) {
      prefetchPromises.push(this.prefetchCurrentUserProfile(userId));
    }

    // Prefetch unread count for badge display
    prefetchPromises.push(this.prefetchUnreadCount());

    // Prefetch first page of activity feed (default landing screen)
    prefetchPromises.push(this.prefetchActivityFeed());

    try {
      await Promise.allSettled(prefetchPromises);
    } catch (error) {
      console.warn('Some prefetch operations failed:', error);
      // Continue app initialization even if prefetch fails
    }
  }

  /**
   * Clear all cached data (useful for logout)
   */
  async clearAllCachedData(): Promise<void> {
    await this.queryClient.clear();
  }

  /**
   * Invalidate user-specific data (useful for user switching)
   */
  async invalidateUserData(): Promise<void> {

    await Promise.all([
      this.queryClient.invalidateQueries({
        queryKey: ['profile'],
      }),
      this.queryClient.invalidateQueries({
        queryKey: NOTIFICATIONS_QUERY_KEYS.all,
      }),
      // Add more user-specific query invalidations here
    ]);

  }
}

/**
 * Utility function to create and get app data prefetcher instance
 */
export function createAppDataPrefetcher(queryClient: QueryClient): AppDataPrefetcher {
  return new AppDataPrefetcher(queryClient);
}

/**
 * Hook to get prefetcher instance from the React context
 */
export function useAppDataPrefetcher(): AppDataPrefetcher {
  const queryClient = useQueryClient();
  return createAppDataPrefetcher(queryClient);
}
